<!-- CmmElectronicPriorAuthorizationQuestionnaire.vue -->
<script lang="ts" setup>
    import '../../../../../css/choice.css'
    import _ from 'lodash';
    import * as choiceApi from '../../../../lib/ChoiceApi'
    import { onBeforeMount, computed, ref, nextTick, reactive } from 'vue';
    import { CoverMyMedsEpaQuestionResponse, CoverMyMedsEpaQuestionItem, CoverMyMedsEpaQuestionChoiceItem, CoverMyMedsQuestionType, AdditionalFreeTextIndicatorType, CoverMyMedsEpaQuestionAnswerItem, ICoverMyMedsEpaQuestionSaveInputs, prettyChoiceText, IPrescriptionDetails, ComparisonType, getQuestionSetAttachmentUrl } from '../../../../lib/Api/CoverMyMeds';
    import { ActionButtonStyle } from '../../../../lib/Types/Types';
    import { Alignment, EnvironmentType, DatabaseIdTypes } from '../../../../Enums/ChoiceEnums';
    import { DisplayStyle, IconTypes } from '../../../../Enums/UiEnums';
    import { FoldingPanel } from '../../../../LibMain';
    import { popAlert, confirmDialog } from '../../../../lib/Notification';
    import { serverEnvironment } from '../../../../lib/Environment';
    import { IntDatabaseIdAndNameModel } from '../../../../lib/Models/IntDatabaseIdAndNameModel';
    import { DatabaseId, IDatabaseIdModel } from '../../../../lib/Models/DatabaseIdModel';
    import { IEntityInfoById } from '../../../../lib/Models/EntityModel';
    import { toDate } from '../../../../lib/DateUtils';
    import { IUserAndGroupTableItem } from '../../../../lib/Models/User/UserAndGroupItem';
    import AdditionalTextIndicator from './AdditionalTextIndicator.vue';
    import CmmQuestionnaireAnswers from './CmmQuestionnaireAnswers.vue';
    import WizardButtons from '../../../../components/Buttons/WizardButtons.vue'
    import TextInput from '../../../../components/TextInput.vue';
    import FloatInput from '../../../../components/FloatInput.vue';
    import DatePickerNoTZ from '../../../../components/DatePickerNoTZ.vue';
    import MessageSection from '../../../../components/Messages/MessageSection.vue';
    import MessageBadge from '../../../../components/Messages/MessageBadge.vue';
    import Checkbox from '../../../../components/Inputs/Checkbox.vue';
    import Icon from '../../../../components/Icon.vue';
    import SectionPanel from '../../../../components/SectionPanel.vue';
    import ChoiceButton from '../../../../components/Buttons/ChoiceButton.vue';
    import FoldingFieldset from '../../../../components/FoldingFieldset.vue';
    import TitledRow from '../../../../components/TitledRow.vue';
    import CmmQuestionnaireAttachment from './CmmQuestionnaireAttachment.vue';
    import ActionButtonV2 from '../../../../components/ActionButtonV2.vue';
    import DateTimePickerNoTZ from '../../../../components/Inputs/DateTimePickerNoTZ.vue';
    import PrescriptionDetails from './PrescriptionDetails.vue';
    import LoadingSection from '../../../../components/LoadingSection.vue';
    import ModalDialog from '../../../../components/ModalDialog.vue';
    import UsersAndGroupsTable from '../../../Comments/UsersAndGroupsTable.vue';
import AlertSection from '../../../../components/Messages/AlertSection.vue';

    const props = withDefaults(defineProps<{
        cmmEpaCallbackId: number,
        hideQuestionnaire?: boolean,
        showDebuggingInfo?: boolean
    }>(), {
        hideQuestionnaire: false
    });

    const emit = defineEmits<{
        answersSent: []
    }>();

    type CoverMyMedsEpaQuestionChoiceItem_PlusUiInfo = 
            CoverMyMedsEpaQuestionChoiceItem & 
            { 
                additionalFreeText?:string;
                selected?:boolean;
            };

    enum AttachmentDestinationType
    {
        QuestionSet,
        Question
    }

    const compData = reactive<{
        questionSet?: CoverMyMedsEpaQuestionResponse,
        patientEntity?: IEntityInfoById,
        currentQuestion?: CoverMyMedsEpaQuestionItem,
        currentChoices: CoverMyMedsEpaQuestionChoiceItem_PlusUiInfo[],

        currentQuestionAnswers: CoverMyMedsEpaQuestionChoiceItem_PlusUiInfo[],
        singleSelectionAnswer?: CoverMyMedsEpaQuestionChoiceItem_PlusUiInfo,
        currentQuestionAnswerText?: string,             // For single selection
        answers: CoverMyMedsEpaQuestionAnswerItem[],
        questionSetAttachmentId?: IntDatabaseIdAndNameModel,             // Attachment for the question set
        currentQuestionAttachmentId?: IntDatabaseIdAndNameModel,             // Attachment for the question set

        showAttachmentModal?: boolean
        attachmentDestination?: AttachmentDestinationType,
        answersSent?: boolean,

        prescriptionDetails?: IPrescriptionDetails,
        usersAndGroups?: IUserAndGroupTableItem[],
        showUserAndGroupModal?: boolean
    }>({
        currentChoices: [],
        currentQuestionAnswers: [],
        answers: []
    });

    const freeText = ref();
    const numericText = ref();
    const datePicker = ref();
    const dateTimePicker = ref();
    const additionalText = ref();

    const sendAnswersSpan = ref();

    onBeforeMount(async () =>{
        // Get prescription info if we're looking at a callback
        compData.prescriptionDetails = await choiceApi.coverMyMeds.getPrescriptionDetails(DatabaseIdTypes.cmm_epa_callback_id, props.cmmEpaCallbackId);
        if (!compData.prescriptionDetails)
        {
            await popAlert("Unable to get prescription details");
            return;
        }

        // Get the question set data
        let resp: (CoverMyMedsEpaQuestionResponse|undefined) = undefined;
        if (!isTestDataset.value)
            resp = await choiceApi.coverMyMeds.getQuestionSet(props.cmmEpaCallbackId);
        else
            resp = await choiceApi.coverMyMeds.getTestQuestionSet(props.cmmEpaCallbackId);

        if (!resp)
        {
            await popAlert("Unable to get question set");
            return;
        }
        // Get a test dataset
        compData.questionSet = resp;

        // Get the patient entity info
        choiceApi.entityInfo.getEntityInfoByDatabaseId(new DatabaseId(patientId.value, DatabaseIdTypes.patient_id))
                        .then(resp => compData.patientEntity = resp);

        compData.currentQuestion = firstQuestion.value;
        compData.currentChoices = getQuestionAnswerOptions();

        initializeQuestion();
    });

    const isTestDataset = computed<boolean>(() => 
            props.cmmEpaCallbackId < 0 && (EnvironmentType.Dev === serverEnvironment() || EnvironmentType.QA ===serverEnvironment()));

    const firstQuestion = computed<CoverMyMedsEpaQuestionItem | undefined>(() => {
        return _.first(_.orderBy(compData.questionSet?.questionList, q => q.sequenceNumber ?? 0));
    });

    function getQuestionAnswerOptions()
    {
        const options = _.orderBy(compData.questionSet?.choiceList?.filter(choice => choice.questionId === compData.currentQuestion?.questionId), option => option.sequenceNumber) ?? [];
        for(const opt of options as CoverMyMedsEpaQuestionChoiceItem_PlusUiInfo[])
        {
            opt.selected = false;
            opt.additionalFreeText = undefined;
        }
        return options;
    }

    // Add to the answer list & then move to the next question
    function goToNextQuestion()
    {
        if (!compData.currentQuestion?.questionId)
        {
            alert("compData.currentQuestion?.questionId is invalid");
            return;            
        }

        switch(compData.currentQuestion.questionType)
        {
            case CoverMyMedsQuestionType.FreeText:
            case CoverMyMedsQuestionType.Numeric:
            case CoverMyMedsQuestionType.Date:
            case CoverMyMedsQuestionType.DateTime:
            addAnswerItem([ { answerValue: compData.currentQuestionAnswerText }]);
            break;

            // Save the selected items as an answer
            case CoverMyMedsQuestionType.SingleSelect:
            case CoverMyMedsQuestionType.MultiSelect:
            const answerValues = compData.currentChoices
                                        .filter(opt => opt.selected)
                                        .map(opt => { return { 
                                            answerValue: opt.choiceId, 
                                            additionalText: opt.additionalFreeText,
                                            multiSelectNextQuestionId: CoverMyMedsQuestionType.MultiSelect === compData.currentQuestion?.questionType ? opt.nextQuestionId : undefined
                                        }});
            addAnswerItem(answerValues);
            break;
        }

        // Figure out what the question is
        let nextQuestionId: string|undefined;
        if (CoverMyMedsQuestionType.SingleSelect === compData.currentQuestion.questionType)
        {
            nextQuestionId = compData.singleSelectionAnswer?.nextQuestionId;
        }
        if (CoverMyMedsQuestionType.Numeric === compData.currentQuestion.questionType)
        {
            if (undefined === compData?.currentQuestionAnswerText)
            {
                alert("The numeric value is undefined");
            }
            else
            {
                nextQuestionId = numericCompareToGetNextQuestion(compData.currentQuestion, +compData.currentQuestionAnswerText);
            }
        }

        // See if we need to continue a multi-select answer chain
        if ("END" === (nextQuestionId ?? "END"))
        {
            const multiSelectAnswerQuesionIds = compData.answers
                                        .flatMap(opt => opt.answerValues)
                                        .map(ans => ans?.multiSelectNextQuestionId)
                                        .filter(qId => qId);

            const questionsNeedingAnswer = multiSelectAnswerQuesionIds
                                        .filter(qid => !compData.answers.find(ans => ans.questionId === qid));
            nextQuestionId = _.first(questionsNeedingAnswer);
        }
        if (!nextQuestionId)
            nextQuestionId = compData.currentQuestion.defaultNextQuestionId;


        // Clear the current answer
        clearAnswer();

        // Get the next question
        compData.currentQuestion = compData.questionSet?.questionList.find(q => q.questionId === nextQuestionId);
        compData.currentChoices = getQuestionAnswerOptions();

        // Initialize the question (it is is valid)
        if (compData.currentQuestion)
            initializeQuestion();
    }

    function addAnswerItem(answerValues: {answerValue?: string, additionalText?: string}[])
    {
        if (!compData.currentQuestion)
        {
            alert("compData.currentQuestion is invalid");
            return;
        }

        compData.answers.push({
            questionSetId: compData.currentQuestion.questionSetId ?? "", 
            questionId: compData.currentQuestion.questionId ?? "",  
            questionType: compData.currentQuestion.questionType,
            answerValues: answerValues,
            attachmentId: compData.currentQuestionAttachmentId
        });

        compData.currentQuestionAttachmentId = undefined;
    }

    function goToPrevQuestion()
    {
        // Are we already on the first question?
        if (1 == compData.answers.length)
        {
            compData.answers = [];
            compData.currentQuestion = _.first(compData.questionSet?.questionList);
            initializeQuestion();
            return;
        }

        // Remove the last question
        const lastQuestion = compData.answers.pop();
        if (!lastQuestion)
            return;

        // Set the current question
        const currentLastQuestionId = _.last(compData.answers)?.questionId;
        if (!currentLastQuestionId)
            return;

        compData.currentQuestion = compData.questionSet?.questionList.find(q => q.questionId === lastQuestion.questionId);

        initializeQuestion();
    }

    function clearAnswer()
    {
        compData.currentQuestionAnswerText = undefined;
        compData.singleSelectionAnswer = undefined;
    }

    function initializeQuestion()
    {
        clearAnswer();
        compData.currentChoices = getQuestionAnswerOptions();

        nextTick(() => {
            switch (compData.currentQuestion?.questionType)
            {
                case CoverMyMedsQuestionType.FreeText:
                    freeText.value?.focus();
                    break;
    
                case CoverMyMedsQuestionType.Numeric:
                    numericText.value?.focus();
                    break;

                case CoverMyMedsQuestionType.Date:
                    datePicker.value?.focus();
                    break;

                case CoverMyMedsQuestionType.DateTime:
                    dateTimePicker.value?.focus();
            }        
        });
    }

    const enableNext_SingleSelectionAdditionalText = computed<boolean>(() =>{
        if (!compData.singleSelectionAnswer)
            return false;

        // Is there additonal text -- only one answer should matter
        if (AdditionalFreeTextIndicatorType.Mandatory === compData.singleSelectionAnswer.additionalFreeTextIndicatorType
            && 0 === (compData.singleSelectionAnswer.additionalFreeText?.length ?? 0))
            return false; 
        return true;
    });

    const enableNext_MultipleSelectionAdditionalText = computed<boolean>(() =>{

        const selectedOptions = compData.currentChoices.filter(opt => opt.selected);
        if (0 === selectedOptions.length)
            return false;

        const needAdditionaText = selectedOptions.filter(opt => {
            if (AdditionalFreeTextIndicatorType.Mandatory === opt.additionalFreeTextIndicatorType && 0 === (opt.additionalFreeText?.length ?? 0))
                return true;
        });
        
        return !needAdditionaText.length;
    });


    function onClickSingleSelectionItem(option: CoverMyMedsEpaQuestionChoiceItem_PlusUiInfo)
    {
        compData.singleSelectionAnswer = option; 
        option.selected = true;

        // Move to the next item if we don't expect any additional free text
        if (option.additionalFreeTextIndicatorType === AdditionalFreeTextIndicatorType.NotAllowed)
        {
            goToNextQuestion();
            return;
        }

        nextTick(() =>{        
            // 
            const inputs = additionalText.value as any[]|undefined;
            if (inputs)
            {
                inputs[0].focus();
            }
        });
    }

    const multiSelectTextInput: Record<string, any>= {};
    function addMultiSelectInput(choiceId: string, comp: any)
    {
        multiSelectTextInput[choiceId] = comp;        
    }

    async function focusOnMultiSelectTextInput(checked: boolean, choiceId: string)
    {
        if (checked) 
        { 
            await nextTick(); 
            multiSelectTextInput[choiceId]?.focus();
        }
    }

    function onClickAddAttachmentIcon()
    {
        compData.attachmentDestination = AttachmentDestinationType.Question;
        compData.showAttachmentModal = true;
    }

    function onAddAttachment(attachmentId: IntDatabaseIdAndNameModel)
    {
        compData.questionSetAttachmentId = attachmentId;
        compData.showAttachmentModal = false;
    }

    async function deleteAttachment()
    {
        if (await confirmDialog('Remove attachment'))
        {
            compData.questionSetAttachmentId = undefined;
        }
    }

    const requireQuestionnaireAttachment = computed<boolean>(() =>
        true === compData.questionSet?.header.attachementRequired
    );

    const patientId = computed<number>(() => 
        compData.questionSet?.questionSet.patientId ?? 0);

    // Send answers for the questionnaire to be processed by CMM
    async function sendAnswers()
    {
        const questionSetAnswers: ICoverMyMedsEpaQuestionSaveInputs = 
        {
            cmmEpaCallbackId: props.cmmEpaCallbackId,
            attachmentId: compData.questionSetAttachmentId,
            questionList: compData.answers.map(ans => {
                return {
                    questionSetId: ans.questionSetId,
                    questionId: ans.questionId,
                    questionType: ans.questionType,
                    attachmentId: ans.attachmentId,
                    answerValues: ans.answerValues?.map(ansVal => {
                        return {answerValue: ansVal.answerValue ?? "", additionalText: ansVal.additionalText}
                    })
                }
            })
        };

        const resp = await choiceApi.coverMyMeds.questionSetSend(questionSetAnswers);
        if (!resp)
        {
            await popAlert("Unable to send answers");
            return;
        }

        if (!resp.passed)
        {
            await popAlert(`Failed to send answers: ${resp.message}`);
            return;
        }
        compData.answersSent = true;
        emit("answersSent");    
    }

    async function resetAnswers()
    {
        const respConfirm = await confirmDialog("Are you sure you want to reset your answers?");
        if (!respConfirm)
            return;
        compData.answers = [];
        compData.currentQuestion = firstQuestion.value;
        initializeQuestion();        
    }

    const assigneeIds = computed<IDatabaseIdModel[]|undefined>(() => {
        if (undefined !== compData.prescriptionDetails?.epa_processor_type_id && undefined !== compData.prescriptionDetails?.epa_processor_id)
            return [{ idType: compData.prescriptionDetails.epa_processor_type_id, id: compData.prescriptionDetails.epa_processor_id }];
        return undefined;
    });

    function numericCompareToGetNextQuestion(question: CoverMyMedsEpaQuestionItem, answerValue: number): string
    {
        for(const singleComparison of question.singleComparisonNextQuestionConditions)
        {
            if (comparisonTrue(singleComparison.comparison, answerValue, singleComparison.comparisonValue))
                return singleComparison.nextQuestionID;
        }

        for(const rangeComparison of question.rangeComparisonNextQuestionConditions)
        {
            const lowerResult = comparisonTrue(rangeComparison.lowerComparison, answerValue, rangeComparison.lowerComparisonValue);
            const upperResult = comparisonTrue(rangeComparison.upperComparison, answerValue, rangeComparison.upperComparisonValue);

            // Between values range
            if ((ComparisonType.GreaterThan === rangeComparison.lowerComparison || ComparisonType.GreaterThanOrEqual === rangeComparison.lowerComparison)
                && (ComparisonType.LessThan === rangeComparison.upperComparison || ComparisonType.LessThanOrEqual === rangeComparison.upperComparison))
            {
                if (lowerResult && upperResult)
                    return rangeComparison.nextQuestionID;
                continue;
            }

            // Range that isn't between -- so one of the two needs to match
            if (lowerResult || upperResult)
            {
                return rangeComparison.nextQuestionID;            
            }
        }

        if (undefined === question.defaultNextQuestionId)
        {
            alert("Unable to determine the next question -- no comparison matched and there was no default next question");
            return "";  // This is a bogus result.
        }

        return question.defaultNextQuestionId;
    }

    function comparisonTrue(comparison: ComparisonType, left: number, right: number): boolean
    {
        switch (comparison)
        {
            case ComparisonType.Equal:
                return left == right;
            case ComparisonType.GreaterThan:
                return left > right;
            case ComparisonType.LessThan:
                return left < right;
            case ComparisonType.GreaterThanOrEqual:
                return left >= right;
            case ComparisonType.LessThanOrEqual:
                return left <= right;
            case ComparisonType.NotEqual:
                return left != right;
            default:
                alert(`Comparison type not handled: ${comparison}`);
                return false;
        }
    }

    const showAttachmentRow = computed(() => {
        return false === (props.hideQuestionnaire ?? false)
            || compData.questionSetAttachmentId;
    });

    const isPastDeadline = computed<boolean>(() => { 
        if (!compData.questionSet?.questionSet.deadline)
            return false;
        return new Date() > new Date(compData.questionSet.questionSet.deadline);
    });

    

</script>
<template>
    <MessageSection v-if="isTestDataset" :displayStyle="DisplayStyle.Danger">
        <Icon :displayStyle="DisplayStyle.Danger" :icon="IconTypes.Warning" />
        This is a test dataset
    </MessageSection>

    <LoadingSection loadingMsg="Loading data" :isLoading="!compData.questionSet" />

    <FoldingFieldset title="Details" v-if="compData.questionSet">
        <PrescriptionDetails v-if="compData.questionSet" 
            :questionSet="compData.questionSet?.questionSet" 
            :prescriber="compData.questionSet.prescriber" 
            :taskItemIdType="DatabaseIdTypes.cmm_epa_callback_id"
            :taskItemId="cmmEpaCallbackId" />        

        <TitledRow title="Assigned To">
            {{ compData.prescriptionDetails?.assigneeName }}
            <Icon :icon="IconTypes.Edit"
                class="pointer"
                v-if="!compData.answersSent && compData.usersAndGroups?.length"
                title="Edit assigned user/group"
                @click="compData.showUserAndGroupModal = true" />
        </TitledRow>            

        <!-- Questionnaire level attachment -->
        <TitledRow title="Attachment" v-if="showAttachmentRow">
            <CmmQuestionnaireAttachment 
                :patientId="patientId"
                :indicateRequired="requireQuestionnaireAttachment"
                v-model:attachmentId="compData.questionSetAttachmentId" 
                :allowRemoval="!compData.answersSent"
                :allowAttachment="!compData.answersSent" />
        </TitledRow>
    </FoldingFieldset>

    <!-- Make sure we're not past the questionnaire deadline -->
    <div v-if="isPastDeadline" class="mt-1">
        <MessageSection :displayStyle="DisplayStyle.Danger">
            Questionnaire Expired
        </MessageSection>
    </div>
    <div v-if="!isPastDeadline">
        <AlertSection v-if="compData.questionSet?.attachmentFileName" :displayStyle="DisplayStyle.Warning" :marginTop="1">
            Questionnaire Attachment: 
            <a target="_blank" :href="getQuestionSetAttachmentUrl(props.cmmEpaCallbackId)">
                <Icon :icon="IconTypes.Attach"/>
                {{ compData.questionSet.attachmentFileName }}
            </a>
        </AlertSection>

        <div v-if="compData.currentQuestion && !props.hideQuestionnaire" class="mt-3">
            <div class="mb-1">
                {{ compData.currentQuestion?.questionText }}    
            </div>
    
            <div v-if="CoverMyMedsQuestionType.FreeText === compData.currentQuestion.questionType">
                <div class="mb-2">
                    <TextInput 
                        ref="freeText" 
                        :maxLength="2000"
                        v-model:textValue="compData.currentQuestionAnswerText" 
                        @keydown.enter="goToNextQuestion()" />
                </div>
    
                <WizardButtons @next="goToNextQuestion()" @prev="goToPrevQuestion()" :enableNext="(compData.currentQuestionAnswerText?.length ?? 0)> 0" />
            </div>
        
            <div v-if="CoverMyMedsQuestionType.Numeric === compData.currentQuestion.questionType">
                <div class="mb-2">
                    <FloatInput ref="numericText" v-model:textValue="compData.currentQuestionAnswerText" :alignment="Alignment.left" @keydown.enter="goToNextQuestion()" />
                </div>
    
                <WizardButtons @next="goToNextQuestion()" @prev="goToPrevQuestion()" :enableNext="(compData.currentQuestionAnswerText?.length ?? 0)> 0" />
            </div>
        
            <div v-if="CoverMyMedsQuestionType.Date === compData.currentQuestion.questionType">
                <div class="mb-2" @keydown.enter="goToNextQuestion">
                    <DatePickerNoTZ ref="datePicker" v-model:value="compData.currentQuestionAnswerText" />
                </div>
        
                <WizardButtons @next="goToNextQuestion()" @prev="goToPrevQuestion()" :enableNext="undefined !== toDate(compData.currentQuestionAnswerText)" />
            </div>
        
            <div v-if="CoverMyMedsQuestionType.DateTime === compData.currentQuestion.questionType">
                <DateTimePickerNoTZ ref="dateTimePicker" v-model="compData.currentQuestionAnswerText"/>
    
                <WizardButtons @next="goToNextQuestion()" @prev="goToPrevQuestion()" :enableNext="undefined !== toDate(compData.currentQuestionAnswerText)" />
            </div>
        
            <!-- Single Option from list of options -->
            <div v-if="CoverMyMedsQuestionType.SingleSelect === compData.currentQuestion.questionType">
                <div v-for="option in compData.currentChoices">
                    <div class="mb-2">
                        <ChoiceButton 
                            text=""
                            :action="ActionButtonStyle.Next" 
                            @click="() => { onClickSingleSelectionItem(option); option.additionalFreeText = undefined; }"/>
        
                        {{ prettyChoiceText(option) }}
                        <AdditionalTextIndicator :type="option.additionalFreeTextIndicatorType" @click="() => onClickSingleSelectionItem(option)" v-if="option.choiceId !== compData.singleSelectionAnswer?.choiceId"/>
    
                        <!-- Show 'Additional Text' when a selection has been made -->
                        <div class="ms-2 mb-2 mt-1" v-if="option.choiceId === compData.singleSelectionAnswer?.choiceId">
                            Additional Text 
                            <AdditionalTextIndicator :type="option.additionalFreeTextIndicatorType" />
        
                            <TextInput ref="additionalText" v-model:textValue="option.additionalFreeText" @keydown.enter="() => {
                                if (enableNext_SingleSelectionAdditionalText)
                                    goToNextQuestion() 
                                }" />
                        </div>
    
                    </div>
                </div>
    
                <WizardButtons @next="goToNextQuestion()" @prev="goToPrevQuestion()" :enableNext="enableNext_SingleSelectionAdditionalText" />
            </div>
        
            <!-- Multiple Option from list of options -->
            <div v-if="CoverMyMedsQuestionType.MultiSelect === compData.currentQuestion.questionType">
                <div v-for="option in compData.currentChoices" :key="option.choiceId">
                    <Checkbox v-model="option.selected" @update:modelValue="checked => { option.selected = checked; focusOnMultiSelectTextInput(checked, option.choiceId); }" />
                    {{ prettyChoiceText(option) }}
    
                    <AdditionalTextIndicator :type="option.additionalFreeTextIndicatorType" v-if="!option.selected" />
    
                    <!-- Show the text input when we need text and the item is selected -->
                    <div v-if="option.selected && AdditionalFreeTextIndicatorType.NotAllowed !== option.additionalFreeTextIndicatorType"
                        class="ms-2 mb-2 mt-1">
                        Additional Text <AdditionalTextIndicator :type="option.additionalFreeTextIndicatorType" />
    
                        <TextInput
                            :maxLength="2000"
                            :ref="comp => addMultiSelectInput(option.choiceId, comp)" 
                            v-model:textValue="option.additionalFreeText" />
                    </div>
                </div>
    
        
                <WizardButtons @next="goToNextQuestion()" @prev="goToPrevQuestion()" :enableNext="enableNext_MultipleSelectionAdditionalText" />
            </div>
        </div>
    
        <div v-if="!compData.currentQuestion">
            <MessageSection v-if="requireQuestionnaireAttachment" :displayStyle="DisplayStyle.Danger">
                Need an attachment attached to complete the questionnaire.
            </MessageSection>
    
            <template v-if="!!compData.questionSet && !requireQuestionnaireAttachment && !compData.answersSent">
                <MessageSection :displayStyle="DisplayStyle.Warning">
                    Please review and send answers
                    <Icon :icon="IconTypes.ChveronDown" @click="() => sendAnswersSpan.scrollIntoView()"  />
                </MessageSection>
        
                <SectionPanel title="Answers">
                    <CmmQuestionnaireAnswers
                        :answers="compData.answers"
                        :questionSet="compData.questionSet" />
                </SectionPanel>
    
                <span ref="sendAnswersSpan">
                    <ActionButtonV2 
                        v-if="!compData.answersSent"
                        :action="ActionButtonStyle.Send"
                        :performAction="sendAnswers" />
    
                    <span class="ms-3">
                        <WizardButtons @prev="goToPrevQuestion()" :showNext="false" />
    
                        <ActionButtonV2 
                            v-if="!compData.answersSent" 
                            :action="ActionButtonStyle.Refresh"
                            :displayStyle="DisplayStyle.Danger"
                            text="Reset"
                            :performAction="resetAnswers" />
                    </span>
    
                </span>
            </template>
            <template v-if="compData.answersSent">
                <MessageSection :displayStyle="DisplayStyle.Success">
                    Answers sent to Cover My Meds!
                </MessageSection>
                <slot name="AnswersSent">
                </slot>
            </template>
        </div>
    </div>


    <template v-if="props.showDebuggingInfo" >
        <hr/>
        <FoldingPanel :panelOpen="false">
            <template #title>Debugging</template>
            <template #body>
                <FoldingPanel :panelOpen="false">
                    <template #title>
                        compData.currentQuestion
                    </template>
                    <template #body>
                        compData.currentQuestion: {{ compData.currentQuestion }}
                    </template>
                </FoldingPanel>
            
                <FoldingPanel :panelOpen="false">
                    <template #title>
                        compData.currentChoices
                    </template>
                    <template #body>
                        <ul v-for="opt in compData.currentChoices">
                            <li>
                                {{ opt }}
                            </li>
                        </ul>
                    </template>
                </FoldingPanel>        
            
                <FoldingPanel>
                    <template #title>
                        Next Question
                    </template>
                    <template #body>
                        {{ 
                            compData.currentChoices
                                .filter(opt => opt.selected)
                                .map(opt => { return { choiceText: opt.choiceText, nextQuestionId: opt.nextQuestionId }; })
                        }}
                    </template>
                </FoldingPanel>
            
                <FoldingPanel v-if="compData.answers.length > 0">
                    <template #title>
                        compData.answers 
                        <MessageBadge :displayStyle="DisplayStyle.Light">
                            {{compData.answers.length}}
                        </MessageBadge>
                    </template>
                    <template #body>
                        <ul v-for="ans in compData.answers">
                            <li>
                                {{ ans }}
                            </li>
                        </ul>
                    </template>
                </FoldingPanel>            
            </template>
        </FoldingPanel>
    </template>

    <ModalDialog v-model:show="compData.showUserAndGroupModal">
        <template #title>Modify Assigned User/Group</template>
        <UsersAndGroupsTable v-if="compData.usersAndGroups"
            :usersAndGroups="compData.usersAndGroups"
            :selectedusersAndGroupsIds="assigneeIds" />
    </ModalDialog>

</template>